import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import { HeroCanvas } from "@/components/HeroCanvas";
import { Marquee } from "@/components/Marquee";
import { CtaBand } from "@/components/CtaBand";
import { CaseStudies } from "@/components/CaseStudies";
import { EASE, MaskedLines, Reveal } from "@/components/Reveal";
import { INDUSTRIES, MANIFESTO, MARQUEE_ITEMS, POSTS, SERVICES, STATS } from "@/data/content";

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo
        title="Vigarbiz — Full-Service Digital Marketing Agency"
        siteName="Vigarbiz"
        image="/logo.png"
        description="Vigarbiz ITES Pvt Ltd & Vigarbiz Inc engineer digital dominance: SEO, paid media, social, content, web engineering and analytics across Pune and Fremont."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Vigarbiz",
          description:
            "Full-service digital marketing agency operating as Vigarbiz ITES Pvt Ltd (India) and Vigarbiz Inc (USA).",
          email: "hello@vigarbiz.com",
        }}
      />

      <section className="relative flex min-h-screen items-center overflow-hidden">
        <HeroCanvas />
        <div className="relative mx-auto w-full max-w-7xl px-4 pb-24 pt-36 sm:px-6 lg:px-8">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, ease: EASE }}
            className="font-mono text-xs uppercase tracking-[0.25em] text-brand"
            data-testid="hero-overline"
          >
            Vigarbiz ITES Pvt Ltd × Vigarbiz Inc — Full-Service Digital Marketing
          </motion.p>

          <h1
            data-testid="hero-title"
            className="mt-6 font-display text-[13.5vw] font-black uppercase leading-[0.95] tracking-tight text-white sm:text-7xl lg:text-8xl"
          >
            <MaskedLines
              lines={[
                "Digital",
                "Velocity,",
                <span key="l3" className="text-brand">Engineered.</span>,
              ]}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
            className="mt-8 max-w-xl text-base leading-relaxed text-muted2 md:text-lg"
          >
            We are the growth engine behind ambitious brands — fusing data, creative and code across two
            continents to turn attention into revenue.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-cta-primary"
              className="inline-flex items-center gap-2 rounded-full bg-brand px-8 py-4 font-display text-base font-bold text-ink transition-colors duration-300 hover:bg-brand-hover active:scale-95"
            >
              Start a Project
              <ArrowRight className="h-5 w-5" />
            </Link>
            <Link
              to="/services"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-8 py-4 font-display text-base font-bold text-white transition-colors duration-300 hover:border-brand hover:text-brand active:scale-95"
            >
              Explore Services
              <ArrowUpRight className="h-5 w-5" />
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
            className="mt-16 grid grid-cols-2 gap-6 border-t border-white/10 pt-8 lg:grid-cols-4"
            data-testid="hero-stats"
          >
            {STATS.map((s) => (
              <div key={s.label}>
                <p className="font-display text-3xl font-black text-white sm:text-4xl">{s.value}</p>
                <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-dim">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      <Marquee items={MARQUEE_ITEMS} />

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-32" data-testid="services-preview">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-brand">What we do</p>
              <h2 className="mt-4 font-display text-4xl font-extrabold uppercase tracking-tight text-white sm:text-5xl lg:text-6xl">
                Six engines.
                <br />
                One machine.
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="services-view-all"
              className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-muted2 transition-colors hover:text-brand"
            >
              All services
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </div>
        </Reveal>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.06}>
              <Link
                to="/services"
                data-testid={`service-card-${s.id}`}
                className="glow-card group flex h-full flex-col rounded-2xl border border-white/10 bg-surface p-6 sm:p-8"
              >
                <div className="flex items-center justify-between">
                  <s.icon className="h-7 w-7 text-brand" strokeWidth={1.5} />
                  <span className="font-mono text-xs text-dim">{s.num}</span>
                </div>
                <h3 className="mt-8 font-display text-xl font-bold text-white transition-colors duration-300 group-hover:text-brand sm:text-2xl">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted2">{s.tagline}</p>
                <span className="mt-auto pt-6 inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.2em] text-dim transition-colors group-hover:text-brand">
                  Deep dive <ArrowUpRight className="h-3 w-3" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-white/10 bg-surface/40 py-20 lg:py-28" data-testid="manifesto-teaser">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-brand">The manifesto</p>
            <h2 className="mt-4 max-w-xl font-display text-4xl font-extrabold uppercase tracking-tight text-white sm:text-5xl">
              Four chapters. Zero fluff.
            </h2>
          </Reveal>
          <div className="mt-12">
            {MANIFESTO.map((m, i) => (
              <Reveal key={m.num} delay={i * 0.05} y={24}>
                <Link
                  to="/about"
                  data-testid={`manifesto-row-${m.num}`}
                  className="group flex items-center gap-6 border-t border-white/10 py-6 last:border-b sm:gap-10"
                >
                  <span className="font-mono text-sm text-brand">{m.num}</span>
                  <span className="font-display text-3xl font-extrabold uppercase tracking-tight text-white/60 transition-[color,transform] duration-500 group-hover:translate-x-3 group-hover:text-white sm:text-5xl">
                    {m.title}
                  </span>
                  <ArrowUpRight className="ml-auto h-7 w-7 shrink-0 text-brand opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CaseStudies />

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28" data-testid="industries-strip">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <h2 className="font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">
              Industries we move
            </h2>
            <Link
              to="/industries"
              data-testid="industries-view-all"
              className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-muted2 transition-colors hover:text-brand"
            >
              All industries
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </div>
        </Reveal>
        <div className="mt-10 flex flex-wrap gap-3">
          {INDUSTRIES.map((ind, i) => (
            <Reveal key={ind.id} delay={i * 0.04} y={16}>
              <Link
                to="/industries"
                data-testid={`industry-chip-${ind.id}`}
                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-surface px-5 py-3 text-sm text-muted2 transition-colors duration-300 hover:border-brand/50 hover:text-white"
              >
                <ind.icon className="h-4 w-4 text-brand" />
                {ind.title}
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-white/10 py-20 lg:py-28" data-testid="insights-preview">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-6">
              <h2 className="font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">
                Latest insights
              </h2>
              <Link
                to="/insights"
                data-testid="insights-view-all"
                className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-muted2 transition-colors hover:text-brand"
              >
                All insights
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            </div>
          </Reveal>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {POSTS.slice(0, 3).map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.07}>
                <Link
                  to="/insights"
                  data-testid={`home-post-${p.slug}`}
                  className="glow-card group block h-full overflow-hidden rounded-2xl border border-white/10 bg-surface"
                >
                  <div className="h-44 overflow-hidden">
                    <img
                      src={p.cover}
                      alt={p.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6">
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand">
                      {p.tag} · {p.readTime}
                    </p>
                    <h3 className="mt-3 font-display text-lg font-bold leading-snug text-white transition-colors group-hover:text-brand">
                      {p.title}
                    </h3>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand sub="Tell us where you want to be in four quarters. We'll bring the machine that gets you there." />
    </div>
  );
}
